import React, { useEffect, useState, useCallback } from 'react';
import classNames from 'classnames';
import { IconClose, IconFile, IconSpinner, Modal, IconArrowHead } from '../../components';
import { getDocumentPreview } from '../../util/api';
import { FormattedMessage } from '../../util/reactIntl';
import css from './UploadedFilesPreview.module.css';

const NO_IMAGE_FORMATS = ['PDF', 'WORD', 'WORDX', 'EXCEL', 'EXCELX', 'DCM', 'PLAIN', 'TXT', 'XLSX', 'DOC', 'DOCX'];

const isImage = (format) => format && !NO_IMAGE_FORMATS.includes(format.toUpperCase());

const isUrlExpired = (url) => {
  try {
    if (!url || !url.includes('?')) return true;
    const params = new URLSearchParams(url.split('?')[1]);
    const expires = parseInt(params.get('X-Amz-Expires'));
    const signedTime = parseInt(params.get('X-Amz-Date')?.replace(/[^0-9]/g, '') || '0');
    const now = Math.floor(Date.now() / 1000);
    return (signedTime + expires) < now;
  } catch (err) {
    console.warn('Error checking URL expiration:', err);
    return true;
  }
};

const UploadedFilesPreview = ({
  rootClassName,
  className,
  files,
  onDeleteFile,
  allowDelete = true,
  folderPath,
  type,
  setSavedDocuments,
  userId,
  onManageDisableScrolling,
  showInPopup = false,
  showDeletePopup = false
}) => {
  const classes = classNames(rootClassName || css.root, className);

  const [previewUrls, setPreviewUrls] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState(null);
  const [previewModalisOpen, setPreviewModalisOpen] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(new Set());

  const [deleteModalisOpen, setDeleteModalisOpen] = useState(false);
  const [deleteFile, setDeleteFile] = useState(false);

  const openPreview = (index) => {
    setCurrentImageIndex(index);
    setPreviewModalisOpen(true);
  };

  const closePreview = () => {
    setCurrentImageIndex(null);
    setPreviewModalisOpen(false);
  };

  const openDeletePopup = (file) => {
    setDeleteFile(file);
    setDeleteModalisOpen(true);
  };

  const closeDeletePopup = () => {
    setDeleteFile(null);
    setDeleteModalisOpen(false);
  };

  const prevImage = useCallback(() => {
    if (files?.length) {
      setCurrentImageIndex((prev) => (prev - 1 + files.length) % files.length);
    }
  }, [files]);

  const nextImage = useCallback(() => {
    if (files?.length) {
      setCurrentImageIndex((prev) => (prev + 1) % files.length);
    }
  }, [files]);

  const handleKeyDown = useCallback(
    (e) => {
      if (!previewModalisOpen) return;
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'Escape') closePreview();
    },
    [previewModalisOpen, prevImage, nextImage]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const fetchPreviewWithRetry = async (key, userId, retries = 2) => {
    try {
      const res = await getDocumentPreview(key, userId);
      return res.url;
    } catch (err) {
      console.error('Error fetching preview for', key, err);

      if ((err.message?.includes('401') || err.status === 401) && retries > 0) {
        console.log(`Retrying preview for ${key}, attempts left: ${retries}`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchPreviewWithRetry(key, userId, retries - 1);
      }

      throw err;
    }
  };

  useEffect(() => {
    const fetchPreviews = async () => {
      const urls = {};
      const newLoadingKeys = new Set();

      await Promise.all(
        files.map(async (f) => {
          if (isImage(f.format)) {

            const existingUrl = previewUrls[f.key];
            if (existingUrl && !isUrlExpired(existingUrl)) {
              urls[f.key] = existingUrl;
              return;
            }

            if (!loadingKeys.has(f.key)) {
              newLoadingKeys.add(f.key);
              try {
                const url = await fetchPreviewWithRetry(f.key, userId);
                urls[f.key] = url;
              } catch (err) {
                console.error('Failed to fetch preview after retries for', f.key, err);
              }
            }
          }
        })
      );

      setPreviewUrls(urls);
      setLoadingKeys(newLoadingKeys);
    };

    if (files?.length && userId) {
      fetchPreviews();
    }
  }, [files, userId]);

  if (!files?.length) return null;

  if (!Object.keys(previewUrls)?.length) {
    return (
      <div className={classes}>
        <div className={classNames(css.file, css.spinner)}>
          <IconSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className={classes}>
      {files.map((f, i) => (
        <div key={f.key} className={css.file} onClick={() => showInPopup ? openPreview(i) : {}}>
          {isImage(f.format) ? (
            <img src={previewUrls[f.key] || ''} alt={f.key} className={css.rootForImage} />
          ) : (
            <div className={css.noImage}>
              <IconFile type={f?.format.toUpperCase()} size="large" />
              <span className={css.fileName}>{f?.name || f.key.replace(`${folderPath}/`, '')}</span>
            </div>
          )}
          {allowDelete && (
            <>
              {showDeletePopup ? (
                <div
                  className={css.removeButton}
                  onClick={() => openDeletePopup(f)}
                >
                  <IconClose />
                </div>
              ) : (
                <div
                  className={css.removeButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSavedDocuments(prev => prev.filter(file => file.key !== f.key));
                    setTimeout(() => {
                      onDeleteFile(f.key, type);
                    }, 0);
                  }}
                >
                  <IconClose />
                </div>
              )}
            </>
          )}
        </div>
      ))}

      <Modal
        id="previewModal"
        className={css.previewModal}
        containerClassName={css.previewModalContainer}
        contentClassName={css.previewModalContent}
        isOpen={previewModalisOpen}
        onClose={closePreview}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.previewModalItem}>
          <div className={css.previewModalPrev} onClick={prevImage}>
            <IconArrowHead direction="left" />
          </div>

          {files?.[currentImageIndex] && (
            <div className={css.previewModalImg}>
              {isImage(files[currentImageIndex].format) ? (
                <img
                  src={previewUrls[files[currentImageIndex].key] || ''}
                  alt={files[currentImageIndex].key}
                  className={css.rootForImage}
                />
              ) : (
                <div className={css.noImage}>
                  <IconFile type={files[currentImageIndex]?.format.toUpperCase()} size="large" />
                  <span className={css.fileName}>
                    {files[currentImageIndex]?.name || files[currentImageIndex].key.replace(`${folderPath}/`, '')}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className={css.previewModalNext} onClick={nextImage}>
            <IconArrowHead direction="right" />
          </div>
        </div>
      </Modal>


      <Modal
        id="deleteModal"
        isOpen={deleteModalisOpen}
        onClose={closeDeletePopup}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.deleteInfo}>

          <p className={css.deleteInfoText}>
            <FormattedMessage id="UploadedFilesPreview.deleteInfoText" />
          </p>

          <div className={css.deleteInfoButtons}>
            <div onClick={closeDeletePopup} className={css.cancelButton}>
              <FormattedMessage id="UploadedFilesPreview.cancel" />
            </div>
            <div
              onClick={(e) => {
                e.stopPropagation();
                setSavedDocuments(prev => prev.filter(file => file.key !== deleteFile.key));
                setTimeout(() => {
                  onDeleteFile(deleteFile.key, type);
                }, 0);
                setDeleteFile(null);
                setDeleteModalisOpen(false);
              }}
              className={css.deleteButton}
            >
              <FormattedMessage id="UploadedFilesPreview.delete" />
            </div>
          </div>

        </div>
      </Modal>
    </div>
  );
};

export default UploadedFilesPreview;
